// src/pages/ClientImport.js - Version avec messages d'erreur détaillés
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Palette de couleurs
const COLORS = {
  primary: '#0B3C5D',
  secondary: '#1E88C8',
  accent: '#4FC3F7',
  darkText: '#2E2E2E',
  lightBg: '#F5F7FA',
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3'
};

// ✅ Fonction pour convertir le type de client (mot → code)
const convertClientTypeToCode = (type) => {
  // Si c'est déjà un chiffre valide (pour rétrocompatibilité)
  if (type === '1' || type === '2') {
    return type;
  }
  
  // Sinon on convertit le mot en code
  const typeMap = {
    'entreprise': '1',
    'entreprises': '1',
    'societe': '1',
    'société': '1',
    'particulier': '2',
    'particuliers': '2',
    'individu': '2',
    'part': '2'  // Abréviation possible
  };
  
  const typeLower = type?.toString().toLowerCase().trim() || '';
  return typeMap[typeLower] || '';
};

// ✅ Fonction pour convertir le statut (mot → code)
const convertStatusToCode = (statut) => {
  // Si c'est déjà un chiffre valide
  if (statut === '0' || statut === '1' || statut === '2') {
    return statut;
  }
  
  // Sinon on convertit le mot en code
  const statusMap = {
    'actif': '1',
    'actifs': '1',
    'inactif': '0',
    'inactifs': '0',
    'suspendu': '2',
    'suspendus': '2'
  };
  
  const statusLower = statut?.toString().toLowerCase().trim() || '';
  return statusMap[statusLower] || '1'; // Par défaut actif
};

// ✅ Fonction pour normaliser le numéro de téléphone (indicatif OBLIGATOIRE)
const normalizePhoneNumber = (phone) => {
  if (!phone) return '';
  
  // Supprimer les espaces
  let cleaned = phone.toString().trim().replace(/\s/g, '');
  
  // 🔥 DOIT commencer par + (comme pour les utilisateurs)
  if (!cleaned.startsWith('+')) {
    throw new Error('Le numéro de téléphone doit commencer par + (indicatif international). Exemple: +243811234567');
  }
  
  // Vérifier qu'après le + il n'y a que des chiffres
  if (!/^\+\d+$/.test(cleaned)) {
    throw new Error('Format de téléphone invalide. Utilisez uniquement des chiffres après le +. Exemple: +243811234567');
  }
  
  return cleaned;
};

const ClientImport = () => {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [currentSocieteId, setCurrentSocieteId] = useState(null);
  const [step, setStep] = useState(1); // 1: Upload, 2: Preview, 3: Results

  // Charger les infos utilisateur
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUserInfo(userData);
        setCurrentSocieteId(userData.societe_id || userData.id_societe || null);
        
        // Vérifier que c'est un gestionnaire (profil 3)
        if (String(userData.profile).trim() !== "3") {
          setError("Seuls les gestionnaires peuvent importer des clients.");
        }
      } catch (error) {
        navigate("/login");
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  // Parser les lignes CSV
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim());
    return result;
  };

  // Lire le fichier CSV
  const parseCSV = (text) => {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return [];
    
    // Ignorer les lignes de commentaires
    const dataLines = lines.filter(line => !line.trim().startsWith('#'));
    
    // Lire les en-têtes
    const headers = dataLines[0]
      .split(',')
      .map(h => h.trim())
      .map(h => h.toLowerCase());
    
    // ✅ En-têtes requis pour les clients
    const requiredHeaders = ['nom_client', 'tel_client', 'type_client'];
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
    
    if (missingHeaders.length > 0) {
      throw new Error(`En-têtes manquants: ${missingHeaders.join(', ')}. Vérifiez le template.`);
    }
    
    const result = [];
    
    // Traiter chaque ligne de données
    for (let i = 1; i < dataLines.length; i++) {
      const line = dataLines[i].trim();
      
      if (!line) continue;
      
      // Parser la ligne CSV
      const values = parseCSVLine(line);
      
      if (values.length !== headers.length) {
        console.warn(`Ligne ${i+1}: Nombre de colonnes incorrect, ignorée`);
        continue;
      }
      
      const obj = {};
      headers.forEach((header, index) => {
        obj[header] = values[index] || '';
      });
      
      result.push(obj);
    }
    
    return result;
  };

  // ========== FONCTION TÉLÉCHARGER MODÈLE - AVEC MOTS COMPRÉHENSIBLES ==========
  const downloadTemplate = () => {
    const headers = [
      'nom_client',        // Nom ou raison sociale (obligatoire)
      'prenom_client',     // Prénom ou nom commercial
      'tel_client',        // Téléphone avec indicatif (obligatoire, format +243...)
      'email_client',      // Email (optionnel)
      'type_client',       // "Entreprise" ou "Particulier" (l'utilisateur voit ça)
      'date_debut_contrat_client', // Date début contrat (YYYY-MM-DD)
      'date_fin_contrat_client',   // Date fin contrat (YYYY-MM-DD)
      'piece_identite_client',     // Pièce d'identité (obligatoire pour particulier)
      'nif_client',        // NIF (obligatoire pour entreprise)
      'rccm_client',       // RCCM (obligatoire pour entreprise)
      'statut_client',     // "Actif", "Inactif" ou "Suspendu" (l'utilisateur voit ça)
      'jours_rappel_fin_contrat' // Jours avant rappel (1-30)
    ];

    // ✅ Exemples avec des MOTS compréhensibles, pas des chiffres !
    const examples = [
      ['SARL Tech Solutions', 'TS Consulting', '+243811234567', 'contact@techsolutions.cd', 'Entreprise', '2024-01-01', '2024-12-31', '', 'NIF-123456789', 'CD/KIN/RCCM/123456', 'Actif', '5'],
      ['Dupont', 'Jean', '+243822345678', 'jean.dupont@email.com', 'Particulier', '2024-02-01', '2024-11-30', 'PASS123456', '', '', 'Actif', '7'],
      ['Global Corp', 'GC Africa', '+243833456789', 'info@globalcorp.cd', 'Entreprise', '2024-03-01', '2024-10-31', '', 'NIF-987654321', 'CD/KIN/RCCM/654321', 'Actif', '3'],
      ['Martin', 'Marie', '+243844567890', 'marie.martin@email.com', 'Particulier', '2024-04-01', '2024-09-30', 'CNI789012', '', '', 'Inactif', '5'],
      ['SARL Construction', 'BTP Services', '+243855678901', 'contact@btp.cd', 'Entreprise', '2024-05-01', '2024-08-31', '', 'NIF-456789123', 'CD/KIN/RCCM/456789', 'Suspendu', '10']
    ];

    const BOM = "\uFEFF";
    
    let csvContent = headers.join(',') + '\n';
    
    examples.forEach(row => {
      csvContent += row.join(',') + '\n';
    });

    const blob = new Blob([BOM + csvContent], { 
      type: 'text/csv;charset=utf-8;' 
    });
    
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'modele_import_clients.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Gestionnaire de fichier
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setError("");
    setPreviewData([]);
    setImportResults(null);
    setStep(1);

    if (!selectedFile) return;

    // Vérifier l'extension
    const fileExt = selectedFile.name.split('.').pop().toLowerCase();
    if (fileExt !== 'csv') {
      setError("❌ Format de fichier non supporté. Utilisez uniquement des fichiers CSV.");
      setFile(null);
      return;
    }

    // Vérifier la taille
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError("❌ Fichier trop volumineux (maximum 10 Mo).");
      setFile(null);
      return;
    }

    // Lire le fichier
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const data = parseCSV(text);
        
        // Limiter à 50 lignes pour la prévisualisation
        const preview = data.slice(0, 50);
        setPreviewData(preview);
        setStep(2);
        
        if (preview.length === 0) {
          setError("❌ Aucune donnée trouvée dans le fichier. Vérifiez le format.");
          setFile(null);
        } else {
          // Vérification rapide du format
          const sample = preview[0];
          if (!sample.nom_client || !sample.tel_client) {
            setError("⚠️ Attention : Le fichier ne semble pas correspondre au format attendu. Vérifiez les en-têtes.");
          }
        }
      } catch (err) {
        console.error("Erreur lecture fichier:", err);
        setError(`❌ Erreur lors de la lecture du fichier: ${err.message}`);
        setFile(null);
      }
    };

    reader.readAsText(selectedFile, 'UTF-8');
  };

  // ✅ Validation des données (accepte les mots ET les codes)
  const validateData = (data) => {
    const errors = [];
    const warnings = [];
    const emails = new Set();
    const nifs = new Set();
    const rccms = new Set();
    
    data.forEach((row, index) => {
      const line = index + 2;
      
      // ===== CHAMPS OBLIGATOIRES POUR TOUS =====
      
      // Nom client (obligatoire)
      if (!row.nom_client || row.nom_client.toString().trim() === '') {
        errors.push(`❌ Ligne ${line}: Le nom du client est obligatoire`);
      }
      
      // Téléphone (obligatoire) - avec indicatif OBLIGATOIRE
      if (!row.tel_client || row.tel_client.toString().trim() === '') {
        errors.push(`❌ Ligne ${line}: Le téléphone est obligatoire`);
      } else {
        const phone = row.tel_client.toString().replace(/\s/g, '');
        
        // ❌ REJETTE TOUT CE QUI NE COMMENCE PAS PAR +
        if (!phone.startsWith('+')) {
          errors.push(`❌ Ligne ${line}: Le téléphone "${row.tel_client}" doit commencer par + (indicatif international). Exemple: +243811234567`);
        } else if (!/^\+\d+$/.test(phone)) {
          errors.push(`❌ Ligne ${line}: Format de téléphone invalide "${row.tel_client}". Utilisez uniquement des chiffres après le +`);
        }
      }
      
      // Type client (obligatoire) - accepte mots ou codes
      const typeOriginal = row.type_client?.toString().toLowerCase().trim() || '';
      if (!typeOriginal) {
        errors.push(`❌ Ligne ${line}: Le type de client est obligatoire`);
      } else {
        // Tenter la conversion
        const typeCode = convertClientTypeToCode(typeOriginal);
        if (!typeCode) {
          errors.push(`❌ Ligne ${line}: Type de client invalide "${row.type_client}". Utilisez: Entreprise ou Particulier`);
        }
      }
      
      // ===== DATES CONTRAT =====
      
      // Date début contrat
      if (row.date_debut_contrat_client && row.date_debut_contrat_client.toString().trim() !== '') {
        const debut = new Date(row.date_debut_contrat_client);
        if (isNaN(debut.getTime())) {
          errors.push(`❌ Ligne ${line}: Date de début invalide "${row.date_debut_contrat_client}" - Format attendu: YYYY-MM-DD`);
        }
      } else {
        warnings.push(`⚠️ Ligne ${line}: Date de début non spécifiée (sera vide)`);
      }
      
      // Date fin contrat
      if (row.date_fin_contrat_client && row.date_fin_contrat_client.toString().trim() !== '') {
        const fin = new Date(row.date_fin_contrat_client);
        if (isNaN(fin.getTime())) {
          errors.push(`❌ Ligne ${line}: Date de fin invalide "${row.date_fin_contrat_client}" - Format attendu: YYYY-MM-DD`);
        }
      } else {
        warnings.push(`⚠️ Ligne ${line}: Date de fin non spécifiée (sera vide)`);
      }
      
      // Validation email
      if (row.email_client && row.email_client.toString().trim() !== '') {
        const email = row.email_client.toString().trim().toLowerCase();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        
        if (!emailRegex.test(email)) {
          errors.push(`❌ Ligne ${line}: Email invalide "${email}"`);
        } else if (emails.has(email)) {
          errors.push(`❌ Ligne ${line}: Email dupliqué dans le fichier "${email}"`);
        } else {
          emails.add(email);
        }
      }
      
      // ===== VALIDATION SPÉCIFIQUE SELON LE TYPE =====
      // On utilise la valeur originale pour déterminer le type
      const typeOriginal2 = row.type_client?.toString().toLowerCase().trim() || '';
      const typeCode = convertClientTypeToCode(typeOriginal2);
      
      if (typeCode === '1') { // Entreprise
        // NIF obligatoire pour entreprise
        if (!row.nif_client || row.nif_client.toString().trim() === '') {
          errors.push(`❌ Ligne ${line}: NIF manquant (obligatoire pour une entreprise)`);
        } else {
          const nif = row.nif_client.toString().trim();
          if (nifs.has(nif)) {
            errors.push(`❌ Ligne ${line}: NIF dupliqué dans le fichier "${nif}"`);
          } else {
            nifs.add(nif);
          }
        }
        
        // RCCM obligatoire pour entreprise
        if (!row.rccm_client || row.rccm_client.toString().trim() === '') {
          errors.push(`❌ Ligne ${line}: RCCM manquant (obligatoire pour une entreprise)`);
        } else {
          const rccm = row.rccm_client.toString().trim();
          if (rccms.has(rccm)) {
            errors.push(`❌ Ligne ${line}: RCCM dupliqué dans le fichier "${rccm}"`);
          } else {
            rccms.add(rccm);
          }
        }
        
      } else if (typeCode === '2') { // Particulier
        // Prénom obligatoire pour particulier
        if (!row.prenom_client || row.prenom_client.toString().trim() === '') {
          errors.push(`❌ Ligne ${line}: Prénom manquant (obligatoire pour un particulier)`);
        }
        
        // Pièce d'identité obligatoire pour particulier
        if (!row.piece_identite_client || row.piece_identite_client.toString().trim() === '') {
          errors.push(`❌ Ligne ${line}: Pièce d'identité manquante (obligatoire pour un particulier)`);
        }
        
        // Pour particulier, NIF et RCCM doivent être vides
        if (row.nif_client && row.nif_client.toString().trim() !== '') {
          errors.push(`❌ Ligne ${line}: Un particulier ne doit pas avoir de NIF. Laissez ce champ vide.`);
        }
        
        if (row.rccm_client && row.rccm_client.toString().trim() !== '') {
          errors.push(`❌ Ligne ${line}: Un particulier ne doit pas avoir de RCCM. Laissez ce champ vide.`);
        }
      }
      
      // Validation statut (optionnel) - accepte mots ou codes
      if (row.statut_client && row.statut_client.toString().trim() !== '') {
        const status = row.statut_client.toString().toLowerCase().trim();
        const statusCode = convertStatusToCode(status);
        if (!statusCode) {
          errors.push(`❌ Ligne ${line}: Statut invalide "${row.statut_client}". Utilisez: Actif, Inactif ou Suspendu`);
        }
      }
      
      // Validation jours rappel (optionnel)
      if (row.jours_rappel_fin_contrat && row.jours_rappel_fin_contrat.toString().trim() !== '') {
        const jours = parseInt(row.jours_rappel_fin_contrat);
        if (isNaN(jours) || jours < 1 || jours > 30) {
          errors.push(`❌ Ligne ${line}: Jours de rappel invalide "${row.jours_rappel_fin_contrat}". Doit être un nombre entre 1 et 30`);
        }
      }
    });

    return { errors, warnings };
  };

  // ✅ Importer les données
  const handleImport = async () => {
    if (!file || previewData.length === 0) {
      setError("❌ Aucune donnée à importer. Veuillez sélectionner un fichier.");
      return;
    }

    const validation = validateData(previewData);
    
    // Afficher les avertissements
    if (validation.warnings.length > 0) {
      console.log("⚠️ Avertissements:", validation.warnings);
    }
    
    // Arrêter s'il y a des erreurs
    if (validation.errors.length > 0) {
      setError(`❌ ${validation.errors.length} erreur(s) de validation détectée(s) :\n${validation.errors.slice(0, 10).join('\n')}`);
      return;
    }

    setImporting(true);
    setError("");
    
    const token = localStorage.getItem("access_token");
    if (!token) {
      setError("❌ Session expirée. Veuillez vous reconnecter.");
      setImporting(false);
      return;
    }

    if (!currentSocieteId) {
      setError("❌ Impossible de déterminer votre société. Contactez l'administrateur.");
      setImporting(false);
      return;
    }

    try {
      const results = {
        created_count: 0,
        duplicate_count: 0,
        error_count: 0,
        quota_count: 0,
        errors: [],
        details: []
      };

      // Pour chaque ligne
      for (let i = 0; i < previewData.length; i++) {
        const row = previewData[i];
        const lineNumber = i + 2;
        
        try {
          // ✅ Convertir les libellés en codes
          const typeCode = convertClientTypeToCode(row.type_client);
          const statusCode = convertStatusToCode(row.statut_client || 'Actif');
          
          // Vérifier la conversion
          if (!typeCode) {
            throw new Error(`Type de client invalide: ${row.type_client}`);
          }

          // ✅ Normaliser le numéro de téléphone (indicatif obligatoire)
          let normalizedPhone = '';
          if (row.tel_client && row.tel_client.toString().trim() !== '') {
            try {
              normalizedPhone = normalizePhoneNumber(row.tel_client);
            } catch (phoneError) {
              throw new Error(`Téléphone: ${phoneError.message}`);
            }
          }

          console.log(`📝 Ligne ${lineNumber}:`, {
            type_original: row.type_client,
            type_converti: typeCode,
            status_original: row.statut_client || 'non spécifié',
            status_converti: statusCode,
            phone: normalizedPhone
          });

          // ✅ Préparer les données pour l'endpoint create.php
          const clientData = {
            nom_client: row.nom_client?.trim() || '',
            prenom_client: row.prenom_client?.trim() || '',
            tel_client: normalizedPhone,                // ← Numéro avec indicatif (+243...)
            email_client: row.email_client?.trim() || '',
            type_client: typeCode,                      // ← "1" ou "2" (converti)
            date_debut_contrat_client: row.date_debut_contrat_client?.trim() || '',
            date_fin_contrat_client: row.date_fin_contrat_client?.trim() || '',
            piece_identite_client: row.piece_identite_client?.trim() || '',
            nif_client: row.nif_client?.trim() || '',
            rccm_client: row.rccm_client?.trim() || '',
            statut_client: statusCode,                  // ← "0", "1" ou "2" (converti)
            jours_rappel_fin_contrat: row.jours_rappel_fin_contrat?.trim() || '5',
            societe_id: currentSocieteId                // ← Société du gestionnaire
          };

          // Nettoyer les données
          Object.keys(clientData).forEach(key => {
            if (typeof clientData[key] === 'string') {
              clientData[key] = clientData[key].trim();
            }
          });

          // Appeler l'endpoint create.php
          const response = await axios.post(
            `${API_BASE_URL}/clients/create.php`,
            clientData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );

          if (response.data?.success) {
            results.created_count++;
            results.details.push({
              line: lineNumber,
              nom: clientData.type_client === '1' ? clientData.nom_client : `${clientData.prenom_client} ${clientData.nom_client}`,
              type: typeCode === '1' ? 'Entreprise' : 'Particulier',
              status: 'success',
              message: '✅ Créé avec succès'
            });
          } else {
            results.error_count++;
            const errorMsg = response.data?.message || 'Erreur inconnue';
            results.errors.push(`❌ Ligne ${lineNumber}: ${errorMsg}`);
            results.details.push({
              line: lineNumber,
              nom: row.nom_client,
              type: row.type_client,
              status: 'error',
              message: errorMsg
            });
          }
        } catch (err) {
          results.error_count++;
          
          let errorMessage = `❌ Ligne ${lineNumber}: `;
          
          if (err.response?.status === 400) {
            errorMessage += err.response.data?.message || 'Données invalides';
            if (err.response.data?.errors) {
              errorMessage += ' (' + err.response.data.errors.join(', ') + ')';
            }
          } else if (err.response?.status === 409) {
            errorMessage += 'Client déjà existant (NIF ou RCCM déjà utilisé)';
            results.duplicate_count++;
          } else if (err.response?.status === 403) {
            if (err.response.data?.code === 'QUOTA_DEPASSE') {
              errorMessage += '❌ QUOTA DÉPASSÉ: Vous avez atteint la limite de clients autorisée';
              results.quota_count++;
            } else if (err.response.data?.code === 'ABONNEMENT_REQUIS') {
              errorMessage += '❌ Abonnement inactif: Vous devez souscrire un abonnement';
            } else {
              errorMessage += 'Permission refusée';
            }
          } else if (!err.response) {
            errorMessage += 'Serveur inaccessible - Vérifiez votre connexion';
          } else {
            errorMessage += err.message || 'Erreur technique';
          }
          
          results.errors.push(errorMessage);
          results.details.push({
            line: lineNumber,
            nom: row.nom_client,
            type: row.type_client,
            status: 'error',
            message: errorMessage
          });
        }

        // Petit délai pour éviter de surcharger le serveur
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Afficher les résultats
      setImportResults(results);
      
      // Message de succès/erreur détaillé
      let resultMessage = '';
      if (results.created_count > 0) {
        resultMessage = `✅ Importation terminée ! ${results.created_count} client(s) créé(s) avec succès.`;
        if (results.quota_count > 0) {
          resultMessage += `\n⚠️ ${results.quota_count} client(s) non créés (quota atteint).`;
        }
        if (results.error_count > 0) {
          resultMessage += `\n❌ ${results.error_count} erreur(s) (détails dans le rapport).`;
        }
        setSuccessMsg(resultMessage);
      } else {
        if (results.quota_count > 0) {
          setError(`❌ Quota clients atteint. ${results.quota_count} client(s) n'ont pas pu être créés.`);
        } else {
          setError(`❌ Aucun client créé. ${results.error_count} erreur(s) détectée(s).`);
        }
      }
      
      setStep(3);
      
    } catch (err) {
      console.error("Erreur import:", err);
      setError("❌ Erreur lors du traitement de l'import: " + err.message);
    } finally {
      setImporting(false);
    }
  };

  // Télécharger le rapport d'erreurs
  const downloadErrors = () => {
    if (!importResults?.details || importResults.details.length === 0) return;
    
    let rapportContent = `=== RAPPORT D'IMPORTATION CLIENTS ===\n`;
    rapportContent += `Date: ${new Date().toLocaleString('fr-FR')}\n`;
    rapportContent += `Fichier: ${file?.name || 'inconnu'}\n`;
    rapportContent += `Société: ${userInfo?.societe || userInfo?.nom_societe || 'inconnue'}\n`;
    rapportContent += `========================================\n\n`;
    rapportContent += `📊 RÉSULTATS:\n`;
    rapportContent += `- ✅ Créés: ${importResults.created_count}\n`;
    rapportContent += `- ⚠️ Doublons: ${importResults.duplicate_count}\n`;
    rapportContent += `- 🔴 Quota atteint: ${importResults.quota_count}\n`;
    rapportContent += `- ❌ Erreurs: ${importResults.error_count}\n`;
    rapportContent += `========================================\n\n`;
    rapportContent += `📝 DÉTAIL PAR LIGNE:\n`;
    
    importResults.details.forEach(detail => {
      let prefix = detail.status === 'success' ? '✅' : '❌';
      rapportContent += `${prefix} Ligne ${detail.line} - ${detail.nom} (${detail.type || '?'}): ${detail.message}\n`;
    });
    
    const blob = new Blob([rapportContent], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rapport_import_${new Date().toISOString().slice(0,10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Réinitialiser
  const handleReset = () => {
    setFile(null);
    setPreviewData([]);
    setError("");
    setSuccessMsg("");
    setImportResults(null);
    setStep(1);
  };

  // Retour à la liste
  const handleCancel = () => {
    navigate("/clients");
  };

  // Vérifier les permissions
  if (!userInfo || String(userInfo.profile).trim() !== "3") {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Import de clients</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/clients">Clients</Link>
              </li>
              <li className="breadcrumb-item active">Import</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
                width: '80px',
                height: '80px',
                backgroundColor: '#ffebee',
                color: COLORS.danger
              }}>
                <i className="bi bi-shield-lock" style={{ fontSize: '2rem' }}></i>
              </div>
              <h5 style={{ color: COLORS.primary, fontWeight: '600' }}>Permission refusée</h5>
              <p className="text-muted">
                Seuls les gestionnaires peuvent importer des clients.
              </p>
              <button
                className="btn mt-3 fw-semibold"
                onClick={() => navigate("/clients")}
                style={{
                  color: COLORS.primary,
                  borderColor: COLORS.primary,
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  padding: '10px 24px'
                }}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Retour
              </button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  // Rendu principal
  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Import de clients</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/clients">Clients</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Import en masse</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <h5 className="card-title" style={{ 
              color: COLORS.primary,
              borderBottom: `2px solid ${COLORS.lightBg}`,
              paddingBottom: '16px',
              marginBottom: '24px'
            }}>
              <i className="bi bi-building me-2" style={{ color: COLORS.secondary }}></i>
              Importer des clients
            </h5>

            {/* Messages */}
            {successMsg && (
              <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e8f5e9',
                borderColor: COLORS.success,
                color: '#2e7d32',
                borderRadius: '8px',
                borderLeft: '4px solid ' + COLORS.success,
                whiteSpace: 'pre-line'
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-check-circle me-2"></i>
                  <div className="flex-grow-1">{successMsg}</div>
                  <button type="button" className="btn-close" onClick={() => setSuccessMsg("")}></button>
                </div>
              </div>
            )}

            {error && (
              <div className="alert alert-danger alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: '4px solid ' + COLORS.danger,
                whiteSpace: 'pre-line'
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{error}</div>
                  <button type="button" className="btn-close" onClick={() => setError("")}></button>
                </div>
              </div>
            )}

            {/* Stepper */}
            <div className="mb-5">
              <div className="d-flex align-items-center justify-content-between mb-4">
                {[1, 2, 3].map((num) => (
                  <div key={num} className="d-flex align-items-center">
                    <div className={`rounded-circle d-flex align-items-center justify-content-center me-3`} style={{ 
                      width: '40px',
                      height: '40px',
                      backgroundColor: step >= num ? COLORS.primary : COLORS.lightBg,
                      color: step >= num ? 'white' : COLORS.darkText,
                      fontWeight: '600'
                    }}>
                      {num}
                    </div>
                    <div>
                      <h6 style={{ 
                        color: step >= num ? COLORS.primary : COLORS.darkText,
                        fontWeight: '600',
                        marginBottom: '4px'
                      }}>
                        {num === 1 ? 'Upload' : num === 2 ? 'Prévisualisation' : 'Résultats'}
                      </h6>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Étape 1: Upload */}
            {step === 1 && (
              <div className="text-center py-4">
                <div className="row justify-content-center">
                  <div className="col-md-8">
                    <div 
                      className="card border-dashed" 
                      style={{ 
                        borderRadius: '12px',
                        border: `2px dashed ${COLORS.accent}`,
                        backgroundColor: '#F8FBFF',
                        padding: '40px 20px',
                        cursor: 'pointer'
                      }}
                      onClick={() => document.getElementById('fileInput').click()}
                    >
                      <input
                        type="file"
                        id="fileInput"
                        accept=".csv"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                      <div className="text-center">
                        <i className="bi bi-cloud-arrow-up" style={{ fontSize: '3rem', color: COLORS.secondary }}></i>
                        <h6 style={{ color: COLORS.primary, fontWeight: '600', marginTop: '1rem' }}>
                          Cliquez pour sélectionner un fichier
                        </h6>
                        <p className="text-muted">ou glissez-déposez votre fichier CSV ici</p>
                        {file && (
                          <span className="badge" style={{ 
                            backgroundColor: '#e8f5e9',
                            color: '#2e7d32',
                            padding: '8px 16px',
                            borderRadius: '20px'
                          }}>
                            {file.name} ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="d-flex justify-content-center mt-4">
                      <button
                        className="btn fw-semibold"
                        onClick={downloadTemplate}
                        style={{
                          color: COLORS.primary,
                          borderColor: COLORS.primary,
                          backgroundColor: 'transparent',
                          borderRadius: '8px',
                          padding: '10px 24px'
                        }}
                      >
                        <i className="bi bi-download me-2"></i>
                        Télécharger le modèle
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Étape 2: Prévisualisation */}
            {step === 2 && previewData.length > 0 && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h6 style={{ color: COLORS.primary, fontWeight: '600' }}>
                    {previewData.length} client(s) à importer
                  </h6>
                  <button
                    className="btn btn-sm fw-semibold"
                    onClick={handleReset}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '6px',
                      padding: '6px 12px'
                    }}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    Changer de fichier
                  </button>
                </div>

                <div className="table-responsive mb-4" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table className="table table-hover table-sm">
                    <thead style={{ backgroundColor: '#F5F7FA', position: 'sticky', top: 0 }}>
                      <tr>
                        {Object.keys(previewData[0] || {}).map((header, index) => {
                          const isRequired = ['nom_client', 'tel_client', 'type_client'].includes(header);
                          return (
                            <th key={index} style={{ 
                              color: COLORS.primary,
                              fontWeight: '600',
                              fontSize: '0.85rem',
                              padding: '12px 8px'
                            }}>
                              {header}
                              {isRequired && <span style={{ color: COLORS.danger, marginLeft: '2px' }}>*</span>}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {Object.values(row).map((value, colIndex) => (
                            <td key={colIndex} style={{ 
                              fontSize: '0.85rem',
                              padding: '8px'
                            }}>
                              {value || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex justify-content-end gap-3 pt-4 border-top">
                  <button
                    className="btn fw-semibold"
                    onClick={handleReset}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '10px 24px'
                    }}
                  >
                    <i className="bi bi-x-circle me-2"></i>
                    Annuler
                  </button>
                  
                  <button
                    className="btn fw-semibold"
                    onClick={handleImport}
                    disabled={importing}
                    style={{
                      color: 'white',
                      backgroundColor: importing ? '#B0BEC5' : COLORS.primary,
                      borderColor: importing ? '#B0BEC5' : COLORS.primary,
                      borderRadius: '8px',
                      padding: '10px 32px'
                    }}
                  >
                    {importing ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Importation...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-cloud-arrow-up me-2"></i>
                        Importer
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Étape 3: Résultats */}
            {step === 3 && importResults && (
              <div className="text-center py-4">
                <div className="mb-4">
                  <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
                    width: '80px',
                    height: '80px',
                    backgroundColor: importResults.created_count > 0 ? '#e8f5e9' : '#ffebee',
                    color: importResults.created_count > 0 ? COLORS.success : COLORS.danger
                  }}>
                    <i className={`bi ${importResults.created_count > 0 ? 'bi-check-circle' : 'bi-exclamation-triangle'}`} style={{ fontSize: '2rem' }}></i>
                  </div>
                  <h5 style={{ color: COLORS.primary, fontWeight: '600' }}>
                    {importResults.created_count > 0 ? 'Importation réussie !' : 'Échec de l\'importation'}
                  </h5>
                  
                  <div className="row justify-content-center mt-4">
                    <div className="col-md-8">
                      <div className="card mb-4">
                        <div className="card-body">
                          <div className="row text-center">
                            <div className="col-3">
                              <h4 style={{ color: COLORS.success }}>{importResults.created_count}</h4>
                              <p className="small">✅ Créés</p>
                            </div>
                            <div className="col-3">
                              <h4 style={{ color: COLORS.warning }}>{importResults.duplicate_count}</h4>
                              <p className="small">⚠️ Doublons</p>
                            </div>
                            <div className="col-3">
                              <h4 style={{ color: COLORS.info }}>{importResults.quota_count}</h4>
                              <p className="small">🔴 Quota</p>
                            </div>
                            <div className="col-3">
                              <h4 style={{ color: COLORS.danger }}>{importResults.error_count}</h4>
                              <p className="small">❌ Erreurs</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {importResults.errors.length > 0 && (
                        <div className="alert alert-warning text-start">
                          <strong>❌ Erreurs détectées :</strong>
                          <ul className="mb-2 mt-2 small">
                            {importResults.errors.slice(0, 10).map((err, i) => (
                              <li key={i}>{err}</li>
                            ))}
                          </ul>
                          {importResults.errors.length > 10 && (
                            <p className="small text-muted">... et {importResults.errors.length - 10} autre(s) erreur(s)</p>
                          )}
                          <button
                            className="btn btn-sm mt-2"
                            onClick={downloadErrors}
                            style={{
                              color: COLORS.warning,
                              borderColor: COLORS.warning,
                              backgroundColor: 'transparent'
                            }}
                          >
                            <i className="bi bi-download me-1"></i>
                            Télécharger le rapport complet
                          </button>
                        </div>
                      )}

                      <div className="d-flex justify-content-center gap-3 mt-4">
                        <button
                          className="btn fw-semibold"
                          onClick={handleReset}
                          style={{
                            color: COLORS.primary,
                            borderColor: COLORS.primary,
                            backgroundColor: 'transparent',
                            borderRadius: '8px',
                            padding: '10px 24px'
                          }}
                        >
                          <i className="bi bi-plus-circle me-2"></i>
                          Nouvel import
                        </button>
                        
                        <button
                          className="btn fw-semibold"
                          onClick={handleCancel}
                          style={{
                            color: 'white',
                            backgroundColor: COLORS.primary,
                            borderColor: COLORS.primary,
                            borderRadius: '8px',
                            padding: '10px 24px'
                          }}
                        >
                          <i className="bi bi-list me-2"></i>
                          Voir la liste
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ClientImport;